import { jsx as _jsx } from "react/jsx-runtime";
import { lazy } from 'react';
import { PageLoading } from 'components/atoms/loading';
import { useAccountDataQuery } from 'queries/account';
import { NotFound } from 'routes/404/404';
import { SecureRoute } from 'routes/route-with-permission-check';
import { ROUTE_PATHS } from 'routes/routes-paths';
import { NewCustomerPortalPage } from 'routes/vendor/lazy-imports';
const CompanyPageTemplateEditPage = /*#__PURE__*/ lazy(()=>import('./company-pages/edit-page').then((module)=>({
            default: module.CompanyPageTemplateEditPage
        })));
const CompanyPagesTemplatesPage = /*#__PURE__*/ lazy(()=>import('./company-pages/page').then((module)=>({
            default: module.CompanyPagesTemplatesPage
        })));
const SheetTemplatePage = /*#__PURE__*/ lazy(()=>import('components/pages/templates/sheet-template-page').then((module)=>({
            default: module.SheetTemplatePage
        })));
// Inlined here instead of importing `sheetTemplateRoutes` from the page file
// directly — that pre-evaluates `<SheetTemplatePage />` at module load and
// pulls the whole sheets subtree (queries + api + utils) into `app`. With a
// React.lazy wrapper above, the page only loads when the route matches.
const sheetTemplateRoutes = {
    path: '/templates/sheet/:id',
    element: /*#__PURE__*/ _jsx(SheetTemplatePage, {})
};
// const EmailPageTemplateEditPage = lazy(() =>
//   import('./email-pages/edit-page').then((module) => ({
//     default: module.EmailPageTemplateEditPage,
//   })),
// );
// const EmailPagesTemplatesPage = lazy(() =>
//   import('./email-pages/page').then((module) => ({
//     default: module.EmailPagesTemplatesPage,
//   })),
// );
const MeetingPageTemplateEditPage = /*#__PURE__*/ lazy(()=>import('./meeting-pages/edit-page').then((module)=>({
            default: module.MeetingPageTemplateEditPage
        })));
const MeetingPagesTemplatesPage = /*#__PURE__*/ lazy(()=>import('./meeting-pages/page').then((module)=>({
            default: module.MeetingPagesTemplatesPage
        })));
// Lazy load all page components
const CpPreviewRoute = /*#__PURE__*/ lazy(()=>import('components/organisms/customer-portal/builder-components/tabs/design-tab/cp-preview-route').then((module)=>({
            default: module.CpPreviewRoute
        })));
const TemplateSheetFieldSectionsPage = /*#__PURE__*/ lazy(()=>import('components/organisms/sheets/template-sheet-field-sections-page').then((module)=>({
            default: module.TemplateSheetFieldSectionsPage
        })));
const DocumentTemplateDetailPage = /*#__PURE__*/ lazy(()=>import('components/organisms/templates/documents/document-detail').then((module)=>({
            default: module.DocumentTemplateDetailPage
        })));
const NewDocumentTemplatePage = /*#__PURE__*/ lazy(()=>import('components/organisms/templates/documents/new-document-detail').then((module)=>({
            default: module.NewDocumentTemplatePage
        })));
const FormTemplateDetailsPage = /*#__PURE__*/ lazy(()=>import('components/organisms/templates/forms/form-details-page').then((module)=>({
            default: module.FormTemplateDetailsPage
        })));
const NewFormTemplatePage = /*#__PURE__*/ lazy(()=>import('components/organisms/templates/forms/new-form-detail').then((module)=>({
            default: module.NewFormTemplatePage
        })));
const NewStatusUpdatesTemplatePage = /*#__PURE__*/ lazy(()=>import('components/organisms/templates/status-updates').then((module)=>({
            default: module.NewStatusUpdatesTemplatePage
        })));
const StatusUpdatesTemplateDetailPage = /*#__PURE__*/ lazy(()=>import('components/organisms/templates/status-updates').then((module)=>({
            default: module.StatusUpdatesTemplateDetailPage
        })));
const CustomerPortalById = /*#__PURE__*/ lazy(()=>import('routes/customer-portal/customer-portal-by-id').then((module)=>({
            default: module.CustomerPortalById
        })));
const CustomerPortalListPage = /*#__PURE__*/ lazy(()=>import('routes/customer-portal/customer-portal-list').then((module)=>({
            default: module.CustomerPortalListPage
        })));
const AiFillTemplatesPage = /*#__PURE__*/ lazy(()=>import('./ai-fill/page').then((module)=>({
            default: module.AiFillTemplatesPage
        })));
const TemplateListRoute = /*#__PURE__*/ lazy(()=>import('./list').then((module)=>({
            default: module.TemplateListRoute
        })));
const ProjectTemplateDetailRoute = /*#__PURE__*/ lazy(()=>import('./projects').then((module)=>({
            default: module.ProjectTemplateDetailRoute
        })));
const ProjectTemplateNewRoute = /*#__PURE__*/ lazy(()=>import('./projects').then((module)=>({
            default: module.ProjectTemplateNewRoute
        })));
const ProjectTemplateSettingsRoute = /*#__PURE__*/ lazy(()=>import('./projects/settings').then((module)=>({
            default: module.ProjectTemplateSettingsRoute
        })));
const SavedAutomationsListPage = /*#__PURE__*/ lazy(()=>import('./saved-automations').then((module)=>({
            default: module.SavedAutomationsListPage
        })));
const SavedAutomationById = /*#__PURE__*/ lazy(()=>import('./saved-automations/by-id').then((module)=>({
            default: module.SavedAutomationById
        })));
const TemplatesRoute = /*#__PURE__*/ lazy(()=>import('.').then((module)=>({
            default: module.TemplatesRoute
        })));
// Wrapper components that guard routes by their respective feature flags
const CompanyPagesTemplatesPageWrapper = ()=>{
    var _ref;
    var _accountData_accountMeta_features, _accountData_accountMeta;
    const { data: accountData, isLoading } = useAccountDataQuery();
    const isDocumentationAgentEnabled = (_ref = accountData === null || accountData === void 0 ? void 0 : (_accountData_accountMeta = accountData.accountMeta) === null || _accountData_accountMeta === void 0 ? void 0 : (_accountData_accountMeta_features = _accountData_accountMeta.features) === null || _accountData_accountMeta_features === void 0 ? void 0 : _accountData_accountMeta_features.documentationAgent) !== null && _ref !== void 0 ? _ref : false;
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoading, {});
    }
    return isDocumentationAgentEnabled ? /*#__PURE__*/ _jsx(CompanyPagesTemplatesPage, {}) : /*#__PURE__*/ _jsx(NotFound, {});
};
const CompanyPageTemplateEditPageWrapper = ()=>{
    var _ref;
    var _accountData_accountMeta_features, _accountData_accountMeta;
    const { data: accountData, isLoading } = useAccountDataQuery();
    const isDocumentationAgentEnabled = (_ref = accountData === null || accountData === void 0 ? void 0 : (_accountData_accountMeta = accountData.accountMeta) === null || _accountData_accountMeta === void 0 ? void 0 : (_accountData_accountMeta_features = _accountData_accountMeta.features) === null || _accountData_accountMeta_features === void 0 ? void 0 : _accountData_accountMeta_features.documentationAgent) !== null && _ref !== void 0 ? _ref : false;
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoading, {});
    }
    return isDocumentationAgentEnabled ? /*#__PURE__*/ _jsx(CompanyPageTemplateEditPage, {}) : /*#__PURE__*/ _jsx(NotFound, {});
};
const MeetingPagesTemplatesPageWrapper = ()=>{
    var _ref;
    var _accountData_accountMeta_features, _accountData_accountMeta;
    const { data: accountData, isLoading } = useAccountDataQuery();
    const isMeetingPagesEnabled = (_ref = accountData === null || accountData === void 0 ? void 0 : (_accountData_accountMeta = accountData.accountMeta) === null || _accountData_accountMeta === void 0 ? void 0 : (_accountData_accountMeta_features = _accountData_accountMeta.features) === null || _accountData_accountMeta_features === void 0 ? void 0 : _accountData_accountMeta_features.meetingPages) !== null && _ref !== void 0 ? _ref : false;
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoading, {});
    }
    return isMeetingPagesEnabled ? /*#__PURE__*/ _jsx(MeetingPagesTemplatesPage, {}) : /*#__PURE__*/ _jsx(NotFound, {});
};
const MeetingPageTemplateEditPageWrapper = ()=>{
    var _ref;
    var _accountData_accountMeta_features, _accountData_accountMeta;
    const { data: accountData, isLoading } = useAccountDataQuery();
    const isMeetingPagesEnabled = (_ref = accountData === null || accountData === void 0 ? void 0 : (_accountData_accountMeta = accountData.accountMeta) === null || _accountData_accountMeta === void 0 ? void 0 : (_accountData_accountMeta_features = _accountData_accountMeta.features) === null || _accountData_accountMeta_features === void 0 ? void 0 : _accountData_accountMeta_features.meetingPages) !== null && _ref !== void 0 ? _ref : false;
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoading, {});
    }
    return isMeetingPagesEnabled ? /*#__PURE__*/ _jsx(MeetingPageTemplateEditPage, {}) : /*#__PURE__*/ _jsx(NotFound, {});
};
// const EmailPagesTemplatesPageWrapper: FC = () => {
//   const { data: accountData, isLoading } = useAccountDataQuery();
//   const { hasAccessToResource } = useWithAccess();
//   const isScarletEnabled = accountData?.accountMeta?.features?.scarlet ?? false;
//   const hasEmailPageTemplateRead = hasAccessToResource({
//     rule: 'EMAIL_PAGE_TEMPLATE:READ',
//   });
//   if (isLoading) {
//     return <PageLoading />;
//   }
//   return isScarletEnabled && hasEmailPageTemplateRead ? (
//     <EmailPagesTemplatesPage />
//   ) : (
//     <NotFound />
//   );
// };
// const EmailPageTemplateEditPageWrapper: FC = () => {
//   const { data: accountData, isLoading } = useAccountDataQuery();
//   const { hasAccessToResource } = useWithAccess();
//   const isScarletEnabled = accountData?.accountMeta?.features?.scarlet ?? false;
//   const hasEmailPageTemplateRead = hasAccessToResource({
//     rule: 'EMAIL_PAGE_TEMPLATE:READ',
//   });
//   if (isLoading) {
//     return <PageLoading />;
//   }
//   return isScarletEnabled && hasEmailPageTemplateRead ? (
//     <EmailPageTemplateEditPage />
//   ) : (
//     <NotFound />
//   );
// };
export const templateRoutes = {
    path: ROUTE_PATHS.TEMPLATES,
    element: /*#__PURE__*/ _jsx(SecureRoute, {
        rule: "TEMPLATE_PAGE:READ",
        children: /*#__PURE__*/ _jsx(TemplatesRoute, {})
    }),
    children: [
        sheetTemplateRoutes,
        {
            path: ROUTE_PATHS.PROJECT_TEMPLATES,
            element: /*#__PURE__*/ _jsx(TemplateListRoute, {}),
            caseSensitive: true
        },
        {
            // projects/:templateId/settings
            path: `${ROUTE_PATHS.PROJECT_TEMPLATES}/${ROUTE_PATHS.TEMPLATE_ID}/${ROUTE_PATHS.SETTINGS}/${ROUTE_PATHS.MATCH_ALL}`,
            element: /*#__PURE__*/ _jsx(ProjectTemplateSettingsRoute, {})
        },
        {
            path: `${ROUTE_PATHS.PROJECT_TEMPLATES}/${ROUTE_PATHS.NEW}/${ROUTE_PATHS.MATCH_ALL}`,
            element: /*#__PURE__*/ _jsx(ProjectTemplateNewRoute, {}),
            caseSensitive: true
        },
        {
            path: `${ROUTE_PATHS.PROJECT_TEMPLATES}/${ROUTE_PATHS.TEMPLATE_ID}/${ROUTE_PATHS.MATCH_ALL}`,
            element: /*#__PURE__*/ _jsx(ProjectTemplateDetailRoute, {}),
            caseSensitive: true
        },
        {
            path: ROUTE_PATHS.DOCUMENT_TEMPLATES,
            element: /*#__PURE__*/ _jsx(TemplateListRoute, {})
        },
        {
            path: `${ROUTE_PATHS.DOCUMENT_TEMPLATES}/${ROUTE_PATHS.NEW}/${ROUTE_PATHS.MATCH_ALL}`,
            // documents/new/*
            element: /*#__PURE__*/ _jsx(NewDocumentTemplatePage, {}),
            caseSensitive: true
        },
        {
            path: `${ROUTE_PATHS.DOCUMENT_TEMPLATES}/${ROUTE_PATHS.TEMPLATE_ID}/${ROUTE_PATHS.MATCH_ALL}`,
            element: /*#__PURE__*/ _jsx(DocumentTemplateDetailPage, {}),
            caseSensitive: true
        },
        {
            path: ROUTE_PATHS.STATUS_UPDATE_TEMPLATES,
            element: /*#__PURE__*/ _jsx(TemplateListRoute, {})
        },
        {
            path: `${ROUTE_PATHS.STATUS_UPDATE_TEMPLATES}/${ROUTE_PATHS.NEW}/${ROUTE_PATHS.MATCH_ALL}`,
            element: /*#__PURE__*/ _jsx(NewStatusUpdatesTemplatePage, {}),
            caseSensitive: true
        },
        {
            path: `${ROUTE_PATHS.STATUS_UPDATE_TEMPLATES}/${ROUTE_PATHS.TEMPLATE_ID}/${ROUTE_PATHS.MATCH_ALL}`,
            element: /*#__PURE__*/ _jsx(StatusUpdatesTemplateDetailPage, {}),
            caseSensitive: true
        },
        {
            path: ROUTE_PATHS.FORM_TEMPLATES,
            element: /*#__PURE__*/ _jsx(TemplateListRoute, {})
        },
        {
            path: `${ROUTE_PATHS.FORM_TEMPLATES}/${ROUTE_PATHS.NEW}/${ROUTE_PATHS.MATCH_ALL}`,
            element: /*#__PURE__*/ _jsx(NewFormTemplatePage, {}),
            caseSensitive: true
        },
        {
            path: `${ROUTE_PATHS.FORM_TEMPLATES}/${ROUTE_PATHS.TEMPLATE_ID}/${ROUTE_PATHS.MATCH_ALL}`,
            element: /*#__PURE__*/ _jsx(FormTemplateDetailsPage, {}),
            caseSensitive: true
        },
        {
            path: ROUTE_PATHS.SAVED_AUTOMATIONS,
            element: /*#__PURE__*/ _jsx(SavedAutomationsListPage, {})
        },
        {
            path: `${ROUTE_PATHS.SAVED_AUTOMATIONS}/${ROUTE_PATHS.TEMPLATE_ID}/${ROUTE_PATHS.MATCH_ALL}`,
            element: /*#__PURE__*/ _jsx(SavedAutomationById, {}),
            caseSensitive: true
        },
        {
            path: `${ROUTE_PATHS.AIFILL_TEMPLATES}`,
            caseSensitive: true,
            element: /*#__PURE__*/ _jsx(AiFillTemplatesPage, {})
        },
        {
            path: `${ROUTE_PATHS.COMPANY_PAGES}`,
            caseSensitive: true,
            element: /*#__PURE__*/ _jsx(SecureRoute, {
                rule: "COMPANY_PAGE_TEMPLATE:READ",
                children: /*#__PURE__*/ _jsx(CompanyPagesTemplatesPageWrapper, {})
            })
        },
        {
            path: `${ROUTE_PATHS.COMPANY_PAGES}/${ROUTE_PATHS.COMPANY_PAGE_ID}`,
            caseSensitive: true,
            element: /*#__PURE__*/ _jsx(SecureRoute, {
                rule: "COMPANY_PAGE_TEMPLATE:READ",
                children: /*#__PURE__*/ _jsx(CompanyPageTemplateEditPageWrapper, {})
            })
        },
        {
            path: `${ROUTE_PATHS.MEETING_PAGES}`,
            caseSensitive: true,
            element: /*#__PURE__*/ _jsx(SecureRoute, {
                rule: "MEETING_PAGE_TEMPLATE:READ",
                children: /*#__PURE__*/ _jsx(MeetingPagesTemplatesPageWrapper, {})
            })
        },
        {
            path: `${ROUTE_PATHS.MEETING_PAGES}/${ROUTE_PATHS.MEETING_PAGE_ID}`,
            caseSensitive: true,
            element: /*#__PURE__*/ _jsx(SecureRoute, {
                rule: "MEETING_PAGE_TEMPLATE:READ",
                children: /*#__PURE__*/ _jsx(MeetingPageTemplateEditPageWrapper, {})
            })
        },
        // {
        //   path: `${ROUTE_PATHS.EMAIL_PAGES}`,
        //   caseSensitive: true,
        //   element: <EmailPagesTemplatesPageWrapper />,
        // },
        // {
        //   path: `${ROUTE_PATHS.EMAIL_PAGES}/${ROUTE_PATHS.EMAIL_PAGE_ID}`,
        //   caseSensitive: true,
        //   element: <EmailPageTemplateEditPageWrapper />,
        // },
        {
            path: `${ROUTE_PATHS.CUSTOMER_PORTALS}`,
            caseSensitive: true,
            element: /*#__PURE__*/ _jsx(CustomerPortalListPage, {})
        },
        {
            path: `${ROUTE_PATHS.CUSTOMER_PORTALS}/create-theme`,
            caseSensitive: true,
            element: /*#__PURE__*/ _jsx(CustomerPortalListPage, {})
        },
        {
            path: `${ROUTE_PATHS.CUSTOMER_PORTALS}/theme-drawer`,
            caseSensitive: true,
            element: /*#__PURE__*/ _jsx(CustomerPortalListPage, {})
        },
        {
            path: `${ROUTE_PATHS.CUSTOMER_PORTALS}/${ROUTE_PATHS.NEW}`,
            caseSensitive: true,
            element: /*#__PURE__*/ _jsx(NewCustomerPortalPage, {})
        },
        {
            path: `${ROUTE_PATHS.CUSTOMER_PORTALS}/${ROUTE_PATHS.ID}`,
            caseSensitive: true,
            element: /*#__PURE__*/ _jsx(CustomerPortalById, {})
        },
        {
            path: `${ROUTE_PATHS.CUSTOMER_PORTALS}/${ROUTE_PATHS.ID}/cp/live-customer-portal-preview/:projectId`,
            caseSensitive: true,
            element: /*#__PURE__*/ _jsx(SecureRoute, {
                children: /*#__PURE__*/ _jsx(CpPreviewRoute, {})
            })
        },
        {
            path: ROUTE_PATHS.SHEET_TEMPLATE,
            element: /*#__PURE__*/ _jsx(TemplateListRoute, {})
        },
        {
            path: ROUTE_PATHS.TEMPLATE_SHEET_FIELDS,
            element: /*#__PURE__*/ _jsx(TemplateSheetFieldSectionsPage, {})
        }
    ]
};
